import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronDown, Check } from 'lucide-react';
import { useClickOutside, useNeedCategories } from "../lib/hooks";

// Tailwind needs full class strings literally so it can't compose them at runtime.
const VARIANT_STYLES = {
  green: {
    searchIcon: "text-green-600",
    containerFocus: "focus-within:border-green-500",
    optionSelected: "bg-green-50 text-green-700 border-green-100",
    checkboxSelected: "bg-green-500 border-green-500 shadow-[0_0_10px_rgba(34,197,94,0.2)]",
    chip: "bg-green-50 text-green-700 border-green-100 hover:bg-green-600 hover:text-black hover:border-green-600",
    chipProtected: "bg-green-100 text-green-800 border-green-200",
  },
  amber: {
    searchIcon: "text-amber-600",
    containerFocus: "focus-within:border-amber-600",
    optionSelected: "bg-amber-50 text-amber-700 border-amber-100",
    checkboxSelected: "bg-amber-600 border-amber-600 shadow-[0_0_10px_rgba(217,119,6,0.2)]",
    chip: "bg-amber-50 text-amber-700 border-amber-100 hover:bg-amber-600 hover:text-white hover:border-amber-600",
    chipProtected: "bg-amber-100 text-amber-800 border-amber-200",
  },
};

export function CategoryDropdown({
  value = [],
  onChange,
  fetchedCategories = [],
  placeholder = "Select NEED categories...",
  disabled = false,
  error,
  variant = "green",
}) {
  const v = VARIANT_STYLES[variant] || VARIANT_STYLES.green;
  const [open, setOpen] = useState(false);
  const { categories } = useNeedCategories();
  const wrapperRef = useRef(null);
  useClickOutside(wrapperRef, () => setOpen(false));

  const safeValue = Array.isArray(value) ? value : [];

  // Categories the source declared are locked in.
  const toggleCategory = (cat) => {
    if (fetchedCategories.includes(cat)) return;
    const newValue = safeValue.includes(cat)
      ? safeValue.filter(c => c !== cat)
      : [...safeValue, cat];
    onChange(newValue);
  };

  const removeCategory = (cat, e) => {
    e.stopPropagation();
    if (fetchedCategories.includes(cat)) return;
    onChange(safeValue.filter(c => c !== cat));
  };
return (
    <div className="relative w-full space-y-3" ref={wrapperRef}>
      <div
        onClick={() => !disabled && setOpen(!open)}
        className={`flex items-center justify-between w-full h-[60px] px-6 border-2 rounded-2xl cursor-pointer bg-white transition-all
          ${disabled ? "bg-slate-50 cursor-not-allowed opacity-60" : ""}
          ${error
            ? "border-red-500 ring-4 ring-red-50"
            : `border-slate-100 hover:border-slate-300 ${v.containerFocus} shadow-sm`}
        `}
      >
        <div className="flex items-center gap-3">
          <div className={`transition-colors ${open ? v.searchIcon : 'text-slate-400'}`}>
             <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
          </div>
          <span className="font-bold text-slate-500">
            {placeholder}
          </span>
        </div>

        <div className="flex items-center pl-2">
          <ChevronDown
            className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      <AnimatePresence>
        {open && !disabled && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            className="absolute z-[100] w-full mt-1 bg-white border border-slate-200 rounded-[2rem] shadow-[0_20px_60px_rgba(0,0,0,0.12)] overflow-hidden"
          >
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto custom-scrollbar">
              {categories.map((cat, index) => {
                const isSelected = safeValue.includes(cat);
                const isProtected = fetchedCategories.includes(cat);
                
                return (
                  <div
                    key={`${cat}-${index}`}
                    onClick={() => toggleCategory(cat)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all group
                      ${isProtected ? "cursor-not-allowed opacity-60" : "cursor-pointer"}
                      ${isSelected
                        ? `border ${v.optionSelected}`
                        : "hover:bg-slate-50 text-slate-600 border border-transparent hover:border-slate-100"}
                    `}
                  >
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all flex-shrink-0
                      ${isSelected
                        ? v.checkboxSelected
                        : "border-slate-200 bg-white group-hover:border-slate-400"}`}
                    >
                      {isSelected && <Check size={12} className="text-white stroke-[4]" />}
                    </div>
                    <span className="text-sm font-semibold select-none truncate">{cat}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex flex-wrap gap-2 pt-1">
        <AnimatePresence mode="popLayout">
          {safeValue.map((cat) => {
            const isProtected = fetchedCategories.includes(cat);
            return (
              <motion.span
                layout
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                key={cat}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black tracking-wider border shadow-sm transition-all
                  ${isProtected
                    ? `${v.chipProtected} cursor-default`
                    : v.chip}`}
              >
                {cat}
                {!isProtected && (
                  <button
                    type="button"
                    onClick={(e) => removeCategory(cat, e)}
                    className="hover:text-red-500 transition-colors p-0.5 rounded-full"
                  >
                    <X size={14} />
                  </button>
                )}
              </motion.span>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}